import { projectLatestCombatTurn, type SharedOpeningEvent } from "../depth/combat-turn";
import type { AbilityEffect, CombatAction, CombatState, CombatStatusKind, CombatTurnEvent, CompanionActionId } from "../depth/types";

export { projectLatestCombatTurn } from "../depth/combat-turn";
export type { CombatTurnSummary } from "../depth/combat-turn";

export type CombatMotionPhase =
  | "intent"
  | "anticipation"
  | "impact"
  | "reaction"
  | "consequence"
  | "settled";

export interface CombatVisualCue {
  id: string;
  actorId: string;
  targetId: string;
  action: CombatAction["type"] | "status";
  actorSide: "heroes" | "enemies";
  amount: number;
  effect: AbilityEffect | null;
  companionActionId?: CompanionActionId | null;
  sharedOpening?: SharedOpeningEvent;
  roadcraftImpact?: {
    readonly kind: "flour-veil" | "millstone-drag";
    readonly preventedDamage: number;
  } | null;
}

export interface CombatMotion {
  phase: CombatMotionPhase;
  actorOffsetX: number;
  actorOffsetY: number;
  targetOffsetX: number;
  effectAlpha: number;
  effectScale: number;
  supportOffsetX?: number;
  braceAlpha?: number;
  weaponArmOffset?: number;
}

export const combatCueDurationSeconds = 1.65;

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function rangeProgress(value: number, start: number, end: number): number {
  return clampUnit((value - start) / (end - start));
}

function phaseAt(progress: number): CombatMotionPhase {
  if (progress < 0.12) return "intent";
  if (progress < 0.32) return "anticipation";
  if (progress < 0.48) return "impact";
  if (progress < 0.72) return "reaction";
  if (progress < 1) return "consequence";
  return "settled";
}

export function projectCombatMotion(
  cue: CombatVisualCue,
  elapsedSeconds: number,
  reducedMotion: boolean,
): CombatMotion {
  const progress = clampUnit(elapsedSeconds / combatCueDurationSeconds);
  const phase = phaseAt(progress);
  const direction = cue.actorSide === "heroes" ? 1 : -1;
  const impactPulse = progress >= 1
    ? 0
    : Math.sin(rangeProgress(progress, 0.3, 0.72) * Math.PI);
  const emphasis = cue.action === "ability" || cue.action === "companion-action" ? 1.28 : cue.action === "guard" ? 0.9 : 1;

  if (cue.action === "joint-action" && cue.sharedOpening?.kind === "shared-opening-spent") {
    // One advance and one impact; the Miller braces rather than striking.
    // Reduced motion keeps the complete resolved tableau until the next turn.
    if (reducedMotion) return {
      phase: "consequence", actorOffsetX: 0, actorOffsetY: 0, targetOffsetX: 0,
      effectAlpha: 0.82, effectScale: 1, supportOffsetX: 0, braceAlpha: 0.9, weaponArmOffset: 1.18,
    };
    const advance = progress < 0.32 ? -4 * rangeProgress(progress, 0.12, 0.32)
      : progress < 0.48 ? -4 + 27 * rangeProgress(progress, 0.32, 0.48)
      : 23 * (1 - rangeProgress(progress, 0.48, 1));
    return {
      phase, actorOffsetX: direction * advance, actorOffsetY: 0,
      targetOffsetX: phase === "reaction" ? direction * 4 * (1 - rangeProgress(progress, 0.48, 0.72)) : 0,
      effectAlpha: impactPulse * 0.94, effectScale: 0.9 + impactPulse * 0.2,
      supportOffsetX: -direction * Math.sin(progress * Math.PI) * 3,
      braceAlpha: progress >= 1 ? 0 : 0.4 + impactPulse * 0.5,
      weaponArmOffset: impactPulse * 1.18,
    };
  }

  if (reducedMotion) {
    return {
      phase,
      actorOffsetX: 0,
      actorOffsetY: 0,
      targetOffsetX: 0,
      effectAlpha: phase === "impact" || phase === "reaction" ? 0.72 : 0,
      effectScale: emphasis,
    };
  }

  if (cue.action === "status") {
    return {
      phase,
      actorOffsetX: 0,
      actorOffsetY: 0,
      targetOffsetX: 0,
      effectAlpha: impactPulse * 0.9,
      effectScale: 0.82 + impactPulse * 0.42,
    };
  }

  if (cue.action === "item") {
    return {
      phase,
      actorOffsetX: 0,
      actorOffsetY: phase === "anticipation" || phase === "impact" ? -3 : 0,
      targetOffsetX: 0,
      effectAlpha: impactPulse * 0.92,
      effectScale: 0.82 + impactPulse * 0.48,
    };
  }

  if (cue.action === "guard") {
    return {
      phase,
      actorOffsetX: 0,
      actorOffsetY: phase === "anticipation" ? -2.5 : 0,
      targetOffsetX: 0,
      effectAlpha: impactPulse * 0.82,
      effectScale: 0.85 + impactPulse * 0.3,
    };
  }

  if (cue.action === "companion-action") {
    const flour = cue.companionActionId === "flour-veil";
    return {
      phase,
      actorOffsetX: flour ? direction * impactPulse * 3 : -direction * impactPulse * 5,
      actorOffsetY: flour ? -impactPulse * 2 : 0,
      targetOffsetX: flour ? 0 : -direction * impactPulse * 3,
      effectAlpha: impactPulse * 0.96,
      effectScale: (0.82 + impactPulse * 0.5) * emphasis,
    };
  }

  let actorOffsetX = 0;
  if (progress >= 0.12 && progress < 0.32) {
    actorOffsetX = -direction * 4 * rangeProgress(progress, 0.12, 0.32);
  } else if (progress >= 0.32 && progress < 0.48) {
    actorOffsetX = direction * (-4 + 21 * rangeProgress(progress, 0.32, 0.48));
  } else if (progress >= 0.48 && progress < 0.72) {
    actorOffsetX = direction * (17 - 11 * rangeProgress(progress, 0.48, 0.72));
  } else if (progress >= 0.72 && progress < 1) {
    actorOffsetX = direction * 6 * (1 - rangeProgress(progress, 0.72, 1));
  }

  const reaction = phase === "reaction"
    ? Math.sin(rangeProgress(progress, 0.48, 0.72) * Math.PI * 5) * Math.min(5, 2 + cue.amount / 12)
    : 0;
  return {
    phase,
    actorOffsetX,
    actorOffsetY: phase === "impact" && cue.action === "ability" ? -2 : 0,
    targetOffsetX: reaction,
    effectAlpha: impactPulse * (cue.action === "ability" ? 0.96 : 0.78),
    effectScale: (0.75 + impactPulse * 0.55) * emphasis,
  };
}

export function combatEffectColor(cue: CombatVisualCue): number {
  if (cue.action === "item") return 0x7fcca5;
  if (cue.action === "guard") return 0x7ab6d9;
  if (cue.companionActionId === "flour-veil") return 0xf4e9c9;
  if (cue.companionActionId === "millstone-drag") return 0xc7b18a;
  return abilityEffectColor(cue.effect);
}

function statusEffect(status: CombatStatusKind): AbilityEffect | null {
  if (status === "burning") return "burning";
  if (status === "poisoned") return "poison";
  if (status === "weakened") return "weaken";
  return null;
}

export function abilityEffectColor(effect: AbilityEffect | null): number {
  if (effect === "arcane") return 0x8db4ff;
  if (effect === "burning") return 0xff8d4d;
  if (effect === "poison") return 0x8fcf64;
  if (effect === "weaken") return 0xb88ad4;
  if (effect === "piercing") return 0xf3e6bc;
  return 0xffc857;
}

export function projectLatestCombatCue(
  combat: CombatState,
  roadcraftImpact: CombatVisualCue["roadcraftImpact"] = null,
): CombatVisualCue | null {
  const summary = projectLatestCombatTurn(combat);
  if (summary === null) return null;
  const actor = combat.combatants.find((candidate) => candidate.id === summary.actorId);
  const statusDamage = summary.statusEvents.find((event): event is Extract<CombatTurnEvent, { kind: "status-tick" | "status-expired" }> =>
    event.kind !== "status-applied" && event.amount > 0
  );
  const guard = summary.statusEvents.find((event) => event.kind === "status-applied" && event.status === "guarding");
  const targetId = summary.damage?.targetId ?? summary.restorative?.targetId ?? summary.companionAction?.targetId ?? statusDamage?.targetId ?? guard?.targetId ?? null;
  if (actor === undefined || targetId === null) return null;
  const action = summary.damage !== null
    ? summary.action
    : summary.restorative !== null
      ? "item"
    : statusDamage !== undefined
      ? "status"
      : summary.companionAction !== null
        ? "companion-action"
      : guard !== undefined
        ? "guard"
        : null;
  if (action === null) return null;
  const effect = action === "joint-action" && summary.sharedOpening?.kind === "shared-opening-spent"
    ? "piercing"
    : action === "companion-action"
    ? summary.companionAction?.effect === "weakened" ? "weaken" : null
    : action === "status"
    ? statusEffect(statusDamage?.status ?? "guarding")
    : summary.abilityId === null
      ? null
      : actor.abilities.find((ability) => ability.id === summary.abilityId)?.effect ?? null;
  return {
    id: summary.id,
    actorId: actor.id,
    targetId,
    action,
    actorSide: actor.side,
    amount: summary.damage?.amount ?? summary.restorative?.amount ?? statusDamage?.amount ?? 0,
    effect,
    companionActionId: summary.companionAction?.companionActionId ?? null,
    ...(summary.sharedOpening === undefined ? {} : { sharedOpening: summary.sharedOpening }),
    roadcraftImpact,
  };
}
